
import { Button, Col, Form, InputGroup, Modal, Offcanvas, Row, Spinner } from "react-bootstrap"
import React, { useEffect, useState, useRef, useMemo } from "react"
import { Controller, useForm, useWatch, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { IconPlus, IconTrash, IconUserPlus, IconX } from "@tabler/icons-react"
import TimezoneSelect, { allTimezones } from "react-timezone-select"
import "react-tagsinput/react-tagsinput.css"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
import { toast } from "sonner"
import { getCities, getStates, getCountries } from "../../Utils/hooks/location"
import { GetRequest, PutRequest } from "../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import { parsePaths } from "react-router-dom"
import Select, { components } from "react-select"
import { customStyles, fetchTemporaryFilterData, getUpdatedFields, showServerError } from "../../Utils/helpers/commonhelper"
import { usePostRequest } from "../../Utils/hooks/QueryHandler"
import { CancelBtn } from "../../CommonComponents/Buttons/CancelBtn"
import { SubmitBtn } from "../../CommonComponents/Buttons/SubmitBtn"
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials"
import { ContactForm } from "../Contacts/ContactModule/ContactForm"

const formSchema = (isEditMode) =>
    z.object({
        deal_name: z.string().min(1, "Deal Name is Required"),
        currency_id: z.string().min(1, "Currency is Required"),
        contact_name: z.string().min(1, "Contact name is required"),
        pipeline: z.string().min(1, "Pipeline is required"),
        pipeline_stage: z.string().min(1, "Pipeline stage is required"),
        owner: z.string().min(1, "Owner is required"),
        expected_closure_date: z.string().nonempty("Date is required"),
        expected_closure_time: z.string().nonempty("Time is required"),
        requirement: z.string().min(1, "Requirement is required"),
        product_services: z.array(z.object({
            product_id: z.string().min(1, "Product is required"),
            quantity: z.preprocess(
                (val) => (typeof val === "number" && Number.isNaN(val) ? undefined : val),
                z.number({
                    required_error: "Quantity is required",
                    invalid_type_error: "Quantity is required",
                })
                    .min(1, "Quantity can't be 0")
            ),

            budget: z.preprocess(
                (val) => (typeof val === "number" && Number.isNaN(val) ? undefined : val),
                z.number({
                    required_error: "Budget is required",
                    invalid_type_error: "Budget is required",
                })
                    .min(0, "Budget must be 0 or more")
                    .multipleOf(0.01, "Maximum 2 decimal places allowed")
            )
        })).min(1, "At least one product item is required"),
        lead_source: isEditMode ? z.string().optional() : z.string().min(1, "Lead source is required"),
        feedback: isEditMode
            ? z.string().optional()
            : z.string().min(1, "Feedback is required").min(5, "Feedback is too short (minimum 5 characters)"),
    })
const productSchema = z.object({
    title: z.string().min(1, "Title is required"),
    price: z
        .string()
        .min(1, "Price is required")
        .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
            message: "Price must be a valid number and at least 0",
        }),
    gst: z.string().min(1, "GST is Required!"),
    hascCode: z.string().min(1, "HASC Code is required"),
});
//menulist for add new products and services button in dropdown
const MenuList = (props) => {
    const { show, canCreate, moduleType, onAddNew } = props.selectProps
    return (
        <>
            <components.MenuList {...props}>
                {props.children}
                {
                    show &&
                    <div
                        style={{
                            padding: "10px",
                            borderTop: "1px solid #ddd",
                            textAlign: "center",
                            cursor: "pointer",
                            color: "#0d6efd",
                            fontWeight: 500,
                        }}
                        onClick={() => {
                            if (!canCreate && (moduleType === "contact" || moduleType === "lead_source" || moduleType === "product_services")) {
                                toast.warning("You are not authorized for this action!")
                                return
                            }
                            // trigger your modal open here
                            onAddNew();
                        }}
                    >
                        + Add New
                    </div>
                }
            </components.MenuList>
        </>
    );
};

// Convert 12-hour time format to 24-hour format
const convert12HourTo24Hour = (time12h) => {
    if (!time12h) return "";

    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
        hours = "00";
    }

    if (modifier === "PM") {
        hours = Number.parseInt(hours, 10) + 12;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

// Dynamic Product Fields Component with forwardRef
const DynamicProductFields = React.forwardRef(({
    control,
    register,
    setValue,
    watch,
    clearErrors,
    trigger,
    errors,
    products,
    setShowModal,
    dealMasterGrant
}, ref) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: "product_services",
    });

    // Add initial field if none exist
    React.useEffect(() => {
        if (fields.length === 0) {
            append({ product_id: "", quantity: 1, budget: 0 });
        }
    }, [fields.length, append]);

    // Expose method to handle new product from modal
    React.useImperativeHandle(ref, () => ({
        addNewProductFromModal: (productId, price) => {
            const currentFields = watch("product_services") || [];

            // Check if there's only one field and it's empty
            const isOnlyDefaultField =
                currentFields.length === 1 &&
                (!currentFields[0].product_id || currentFields[0].product_id === "");

            if (isOnlyDefaultField) {
                // Set to the first field if it's empty
                setValue(`product_services.0.product_id`, String(productId));
                setValue(
                    `product_services.0.budget`,
                    Number(price),
                    { shouldValidate: true }
                );
                clearErrors(`product_services.0.budget`);
            } else {
                // Add as new field if products already exist
                append({
                    product_id: String(productId),
                    quantity: 1,
                    budget: Number(price),
                });
            }
        },
    }));

    const addMoreFields = () => {
        append({ product_id: "", quantity: 1, budget: 0 });
    };

    const removeFields = (index) => {
        if (fields.length > 1) {
            remove(index);
        }
    };

    const fieldsArr = useWatch({ control, name: "product_services" }) || [];
    // const getFilteredOptions = (currentIndex) => {
    //     const selectedProductIds = fieldsArr
    //         ?.map((item, idx) => (idx !== currentIndex ? item?.product_id : null))
    //         ?.filter(Boolean);

    //     return products.filter(
    //         (option) => !selectedProductIds.includes(String(option.value))
    //     );
    // };

    return (
        <div>
            {fields.map((field, index) => (
                <div key={field.id} className={index > 0 ? "" : ""}>
                    <Row>
                        {/* Product Selection */}
                        <Col md={6}>
                            <Form.Group className="mb-3 focus-z-index position-relative" controlId={`product-${index}`}>
                                <Form.Label>
                                    Product and Services <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                    name={`product_services.${index}.product_id`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            className="basic-single"
                                            options={products.map((product) => ({
                                                value: product.id,
                                                label: product.service
                                            }))}
                                            value={products.length ? products.find(p => String(p.id) === String(field.value)) ? {
                                                value: field.value,
                                                label: products.find(p => String(p.id) === String(field.value))?.service
                                            } : null : null}
                                            onChange={(selectedOption) => {
                                                const selectedId = selectedOption ? selectedOption.value : "";
                                                field.onChange(selectedId);

                                                if (selectedId) {
                                                    const selectedProduct = products.find((p) =>
                                                        String(p.id) === String(selectedId)
                                                    );
                                                    if (selectedProduct) {
                                                        setValue(`product_services.${index}.budget`, selectedProduct.price);
                                                    }
                                                } else {
                                                    setValue(`product_services.${index}.budget`, undefined);
                                                }
                                            }}
                                            styles={customStyles}
                                            isSearchable={true}
                                            isClearable={true}
                                            classNamePrefix="react-select"
                                            components={{ MenuList }}
                                            show={true}
                                            canCreate={dealMasterGrant.isCreate}
                                            moduleType={'product_services'}
                                            onAddNew={() => setShowModal(true)}
                                            placeholder="Select Product and services"
                                        />
                                    )}
                                />
                                {errors.product_services?.[index]?.product_id && (
                                    <span className="text-danger fs-12 mt-1">
                                        {errors.product_services[index].product_id.message}
                                    </span>
                                )}
                            </Form.Group>
                        </Col>
                        {/* Quantity */}
                        <Col md={3}>
                            <Form.Group className="mb-3 focus-z-index position-relative" controlId={`quantity-${index}`}>
                                <Form.Label>Quantity <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    placeholder="Enter Quantity"
                                    {...register(`product_services.${index}.quantity`, {
                                        valueAsNumber: true,
                                    })}
                                />
                                {errors.product_services?.[index]?.quantity && (
                                    <span className="text-danger mt-1 fs-12">
                                        {errors.product_services[index].quantity.message}
                                    </span>
                                )}
                            </Form.Group>
                        </Col>
                        {/* Budget */}
                        <Col md={3}>
                            <Form.Group className="mb-3 d-flex align-items-center  focus-z-index position-relative" controlId={`budget-${index}`}>
                                <div className="w-100">
                                    <Form.Label>
                                        Budget <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Enter Budget"
                                        step="0.01"
                                        {...register(`product_services.${index}.budget`, {
                                            setValueAs: (v) => (v === "" ? undefined : Number(v)),
                                        })}
                                    />
                                    {errors.product_services?.[index]?.budget && (
                                        <span className="text-danger mt-1 fs-12">
                                            {errors.product_services[index].budget.message}
                                        </span>
                                    )}
                                </div>

                                {/* Remove button (only for rows after first) */}
                                {index > 0 && (
                                    <button
                                        type="button"
                                        style={{
                                            width: "28px",
                                            height: "28px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            border: "0.4px solid red",
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            marginLeft: "8px",
                                            marginBottom: "4px"
                                        }}
                                        onClick={() => removeFields(index)}
                                    >
                                        <IconTrash
                                            stroke={2}
                                            style={{ stroke: "red" }}
                                            className="w-3 h-3"
                                        />
                                    </button>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            ))}

            {/* Add More Button */}
            <div className="text-end mb-3">
                <button
                    type="button"
                    style={{
                        fontSize: "14px",
                        border: 0,
                        color: "#777",
                        background: "none",
                    }}
                    onClick={addMoreFields}
                >
                    Add Product
                </button>
                <IconPlus stroke={2} style={{ width: '17px', height: '17px' }} ></IconPlus>
            </div>

            {/* Show validation error for the entire array */}
            {
                errors.product_services && !Array.isArray(errors.product_services) && (
                    <div className="text-danger text-center  mt-1 fs-12">
                        {errors.product_services.message}
                    </div>
                )
            }
        </div >
    );
});

export const UpdateDeal = ({
    show,
    setShow,
    onHide,
    editId,
    refetchDeals,
    module_name,
    contactArray,

    currAdvFilter,
    applyFilterDataParams,
    setCurrAdvFilter,
    setFilterState,
    setTempFilterData,
    setCustomFilter,
    setDealsFilterValues,
}) => {
    const { customer, setCustomer, headers, extras, customerId: customer_id, loginUserId, userType } = useGetEssentials()

    // Edit deal form component
    const [contactDetails, setContactDetails] = useState(null);
    const [contactOptions, setContactOptions] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState("");
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [country, setCountries] = useState([{ countryName: "", countryId: 0 }]);
    const [states, setStates] = useState([{ stateName: "", stateCode: 0 }]);
    const [city, setCity] = useState([{ cityName: "", cityCode: 0 }]);
    const [products, setProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [leadSource, setLeadSource] = useState([]);
    const [pipeLine, setPineLineStage] = useState([]);
    const [isContactSelected, setIsContactSelected] = useState(false);
    // new contact add form state
    const [contactShow, setShowContact] = useState(false)
    // state for recent contact added directly from add new
    const [recentContact, setRecentContact] = useState({
        recentContactId: "",
        recentContactName: "",
    });
    const [expectedDate, setExpectedDate] = useState("");
    const [expectedTime, setExpectedTime] = useState("");
    const [hasFormChanges, setHasFormChanges] = useState(false);
    const [originalFormData, setOriginalFormData] = useState(null);
    const [selectedPipelineId, setSelectedPipelineId] = useState(null);
    const [availablePipelines, setAvailablePipelines] = useState([]);
    const [currencyOpt, setCurrencyOpt] = useState([])
    const [isOwnerSame, setIsOwnerSame] = useState(false)
    const [showExtraSections, setShowExtraSections] = useState(true)

    const [editDealData, setEditDealData] = useState(null)
    // state for blocking the interaction with the form until the data is being fetched for update
    const [isDataFetching, setIsDataFetching] = useState(false);
    // loading state when the form is being submitted
    const [isSaving, setIsSaving] = useState(false)

    const [latestProductId, setLatestProductId] = useState(null);

    // Create ref for DynamicProductFields
    const productFieldsRef = useRef(null);

    const loginUserName = `${customer?.customer_details?.first_name || ""} ${customer?.customer_details?.last_name || ""}`;

    const {
        register,
        control,
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        trigger,
        watch,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(formSchema(true)), // Pass true for isEditMode
        defaultValues: {
            owner: "",
            product_services: [],
        },
    });

    // for product and services form
    const {
        register: registerProduct,
        handleSubmit: handleSubmitProduct,
        reset: resetProduct,
        formState: { errors: productErrors },
    } = useForm({
        resolver: zodResolver(productSchema),
    });

    const feedbackValue = useWatch({ control, name: "feedback" });

    // Watch all form values to detect changes
    const watchedValues = watch();

    const [PERMISSION, _] = useLocalStorageState('permission')
    const dealContactGrant = PERMISSION.contact;
    const dealMasterGrant = PERMISSION.setting.feature.master

    // customized contact options
    const customContactOptions = useMemo(
        () =>
            contactOptions.map((conta) => ({
                value: conta.id,
                label: conta.label,
                company_name: conta.company,
            })),
        [contactOptions]
    );

    // Check if form has changes compared to original data
    useEffect(() => {
        if (originalFormData) {
            const hasChanges = Object.keys(originalFormData).some((key) => {
                if (key === 'product_services') {
                    return JSON.stringify(watchedValues[key]) !== JSON.stringify(originalFormData[key]);
                }
                return watchedValues[key] !== originalFormData[key];
            });
            setHasFormChanges(hasChanges);
        }
    }, [watchedValues, originalFormData]);

    // Clear errors when fields are properly filled
    useEffect(() => {
        if (feedbackValue && feedbackValue.length >= 5) {
            clearErrors("feedback");
        }
    }, [feedbackValue, clearErrors]);

    // fetch deals pipeline
    useEffect(() => {
        const fetchPipelines = async () => {
            try {
                const res = await GetRequest(`lead/fetchPipelines_dropdown/${customer_id}/deals/${loginUserId}/${userType}`, { headers }, extras);
                if (res?.status === "success") {
                    setAvailablePipelines(res.data || []);
                }
            } catch (error) {
                console.error("Failed to fetch pipelines:", error);
            }
        };

        if (show && customer_id) {
            fetchPipelines();
        }
    }, [show, customer_id]);

    // fetch deal details by the id passed and hydrate within the form
    useEffect(() => {
        const fetchDealById = async () => {
            if (!editId) return;
            setIsDataFetching(true)
            try {
                const res = await GetRequest(`deals/getDealDataForUpdate/${editId}/${customer_id}/${loginUserId}`, { headers }, extras);
                if (res.status === "fail" || res.success === false) {
                    showServerError()
                    onHide()
                    return
                }
                const dealData = res.data[0];
                setEditDealData(dealData)
                if (dealData) {
                    if (!recentContact.recentContactId || recentContact.recentContactId?.trim() !== "") {
                        setSelectedContactId(dealData?.contact_id);
                    }
                    setIsContactSelected(true);
                    if (
                        userType === "1"
                    ) {
                        setIsOwnerSame(true)
                    } else if (loginUserId === dealData?.owner_id) {
                        setIsOwnerSame(true)
                    } else {
                        setIsOwnerSame(false)
                    }
                    // Set pipeline ID from deal data
                    const dealPipelineId = dealData.pipeline_id;
                    setSelectedPipelineId(dealPipelineId);

                    setExpectedDate(dealData.expected_closure_date);
                    setExpectedTime(convert12HourTo24Hour(dealData.expected_closure_time));
                    const parsedProducts = JSON.parse(dealData?.product_services || "[]");
                    const finalProductArray = parsedProducts.map(c => ({ ...c, quantity: Number(c.quantity) || 1 }))

                    // parsedProducts.map(c => [{...c, }])
                    const formData = {
                        deal_name: dealData.deal_name,
                        currency_id: dealData?.currency_id,
                        contact_name: dealData?.contact_id,
                        owner: dealData.owner_id,
                        pipeline: String(dealPipelineId),
                        pipeline_stage: dealData.pipeline_stage_id,
                        expected_closure_date: dealData.expected_closure_date,
                        expected_closure_time: convert12HourTo24Hour(dealData.expected_closure_time),
                        requirement: dealData.requirement,
                        lead_source: dealData.lead_source_id,
                        follow_up_date: dealData.followup_date,
                        follow_up_time: dealData.followup_time,
                        feedback: dealData.feedback || "",
                        product_services: finalProductArray
                    };
                    console.log('formData', formData)
                    // Store original data for change detection
                    setOriginalFormData(formData);
                    reset(formData);
                    clearErrors();

                    setTimeout(() => {
                        fetchContactDetailById(dealData?.contact_id);
                    }, 200);
                }
            } catch (err) {
                toast.error("Failed to fetch deal");
            } finally {
                setIsDataFetching(false);
            }
        };
        fetchDealById();
    }, [editId, show]);

    useEffect(() => {
        setIsContactSelected(!!selectedContactId && selectedContactId !== "");
    }, [selectedContactId]);

    // Updated useEffect for handling new products
    useEffect(() => {
        if (latestProductId && products.length > 0 && productFieldsRef.current) {
            const selectedProduct = products.find(
                (p) => String(p.id) === String(latestProductId)
            );
            if (selectedProduct) {
                productFieldsRef.current.addNewProductFromModal(
                    latestProductId,
                    selectedProduct.price
                );
                setLatestProductId(null);
            }
        }
    }, [products, latestProductId]);

    // Scroll to specific section in form
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const formContainer = document.querySelector(".form-container");
            formContainer.scrollTo({
                top: element.offsetTop - 110,
                behavior: "smooth",
            });
        }
    };

    const fecthPipeLine = async () => {
        try {
            const res = await GetRequest(`/lead/fetchPipelineStage/${selectedPipelineId}/${customer_id}`, { headers }, extras);
            if (res.status === "success") setPineLineStage(res.data);
        } catch (err) {
            console.error("Failed to fetch pipeline stages:", err);
        }
    };

    // Refetch pipeline stages when selected pipeline changes
    useEffect(() => {
        if (selectedPipelineId && customer_id && show) {
            fecthPipeLine();
        }
    }, [selectedPipelineId, customer_id, show]);

    // Handle form submission
    const onSubmit = async (data) => {
        // Only proceed if there are changes
        if (!hasFormChanges) {
            handleClose();
            return;
        }
        try {
            const payload = {
                deal_id: editId,
                currency_id: data.currency_id,
                customer_id: customer_id,
                deal_name: data.deal_name,
                contact_id: selectedContactId,
                pipeline_id: selectedPipelineId,
                pipeline_stage_id: data.pipeline_stage,
                expected_closure_date: data.expected_closure_date,
                expected_closure_time: data.expected_closure_time,
                requirement: data.requirement,
                product_services: data.product_services,
                lead_source_id: data.lead_source,
                user_login_id: loginUserId,
                owner_id: data.owner
            };
            const finalPayload = {
                ...payload,
                updating_field: getUpdatedFields(editDealData, payload)
            }

            const res = await PutRequest(`/deals/updateDeal`, finalPayload, { headers }, extras);

            if (res?.status === "success") {
                toast.success(res.message);
                if (currAdvFilter === "apply") {
                    fetchTemporaryFilterData(applyFilterDataParams?.applyFilterData)
                } else {
                    setCurrAdvFilter && setCurrAdvFilter(null)
                    setFilterState && setFilterState([])
                    setTempFilterData && setTempFilterData(null)
                    setCustomFilter && setCustomFilter(null)
                    setDealsFilterValues && setDealsFilterValues({ filter_values: [], filter_state: [] })
                    refetchDeals && refetchDeals();
                }
                handleClose();
            } else {
                toast.error(res.message);
            }
        } catch (err) {
            console.error("Error submitting form:", err);
            toast.error("Failed to update deal");
        }
    };

    // Fetch owner options when component mounts
    useEffect(() => {
        const fetchOwnerOptions = async () => {
            try {
                const res = await GetRequest(`/users/users/fetchUserAsOwner/${customer_id}`, { headers }, extras);

                let data = [];

                if (res?.status === "success") {
                    data = res.data.map((user) => ({
                        id: user.id,
                        label: user.full_name,
                    }));
                }

                const isLoginUserIncluded = data.some((user) => user.id === loginUserId);

                if (!isLoginUserIncluded) {
                    data.unshift({ id: loginUserId, label: loginUserName });
                }

                setOwnerOptions(data);
            } catch (error) {
                const fallback = [{ id: loginUserId, label: loginUserName }];
                setOwnerOptions(fallback);
            }
        };

        if (show) {
            fetchOwnerOptions();
        }
    }, [customer, show]);

    // Fetch contact options when form opens
    useEffect(() => {
        const fetchContactNames = async () => {
            const res = await GetRequest(`lead/getContactsForLeads/${customer_id}/${loginUserId}/${userType}`, headers, extras);
            if (res.status === "success") {
                const data = res.data.map((contactName) => ({
                    id: contactName.id,
                    label: contactName.contact_full_name,
                }));

                setContactOptions(data);

                res.data &&
                    res.data.find((c) => {
                        if (c.id === recentContact.recentContactId) {
                            setValue("contact_name", recentContact.recentContactId);
                            setSelectedContactId(recentContact.recentContactId)
                            setRecentContact((prev) => ({
                                ...prev,
                                recentContactName: c.contact_full_name,
                            }));
                        }
                    });
            }

        };

        if (show || recentContact.recentContactId) {
            fetchContactNames();
        }
    }, [show, recentContact.recentContactId]);

    // useeeffect for fetching currency value dropdown.
    useEffect(() => {
        (async () => {
            const res = await GetRequest(`deals/fetchActiveCurrency/${customer_id}`, { headers }, extras)
            if (res.status === "fail" || res.success === false) {
                return
            }

            const currencyOptions = res.data && res.data?.map(item => {
                try {
                    // Parse the currency string to object
                    const currencyObj = JSON.parse(item.currency);

                    // Create React Select option
                    return {
                        value: item.id, // Using the item's id as value
                        label: `${currencyObj.label} (${currencyObj.value})` // Format: "Chilean Peso (CLP)"
                    };
                } catch (error) {
                    console.error('Error parsing currency:', error);
                    return null;
                }
            }).filter(option => option !== null); // Remove any null entries from failed parsing

            setCurrencyOpt(currencyOptions)
        })()
    }, [])

    // Fetch contact details by ID
    const fetchContactDetailById = async (contactSelected = selectedContactId) => {
        if (!contactSelected) {
            setContactDetails(null);
            return;
        }
        try {
            const res = await GetRequest(`lead/getContactDetailsForLeads/${contactSelected}`, headers, extras);
            if (res.status === "fail" || res.success === false) {
                setContactDetails([])
                setIsContactSelected(false)
                return
            }
            if (res.status === "success" && res.data?.length > 0) {
                setContactDetails(res.data[0]);
                setIsContactSelected(true);
            }
        } catch (error) {
            // Error handling
            return
        }
    };

    useEffect(() => {
        fetchContactDetailById();
    }, [selectedContactId]);

    // Fetch countries when component mounts
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await getCountries();
                if (data) {
                    setCountries(
                        data.map((c) => ({
                            countryName: c.name,
                            countryId: c.id,
                        })),
                    );
                }
            } catch (err) {
                return
                // Error handling
            }
        };
        if (show) {
            fetchCountries();
        }
    }, [show]);

    // Fetch states when country changes
    useEffect(() => {
        if (!contactDetails?.country) return;

        const fetchStates = async () => {
            try {
                const data = await getStates(contactDetails.country);
                if (data) {
                    setStates(
                        data.map((s) => ({
                            stateName: s.state_name,
                            stateCode: s.id,
                        })),
                    );
                }
            } catch (err) {
                // Error handling
                return
            }
        };
        fetchStates();
    }, [contactDetails?.country]);

    // Fetch cities when state changes
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const data = await getCities(contactDetails.state);
                if (data) {
                    const mapped = data.map((city) => ({
                        cityName: city.city_name,
                        cityCode: city.id,
                    }));

                    setCity(mapped);
                }
            } catch (error) {
                toast.error("Error Fetching Cities");
                return
            }
        };

        if (contactDetails?.state) fetchCities();
    }, [contactDetails?.state]);

    // Helper functions to get location names
    const getCountryName = (id) => {
        const found = country.find((c) => String(c.countryId) === String(id));
        return found ? found.countryName : "";
    };

    const getStateName = (id) => {
        const found = states.find((s) => String(s.stateCode) === String(id));
        return found ? found.stateName : "";
    };

    const getCityName = (id) => {
        const found = city.find((c) => String(c.cityCode) === String(id));
        return found ? found.cityName : "";
    };

    // Fetch products when form opens
    const fetchProducts = async () => {
        try {
            const res = await GetRequest(`masters/getProducts/${customer_id}`, { headers }, extras);

            if (res?.data) {
                setProducts(res.data);
            }
        } catch (error) {
            // Error handling
        }
    };

    const { mutate: addProduct } = usePostRequest(
        `/masters/createProduct`,
        null,
        { headers },
        extras
    );
    // Handle product form submission
    const onProductSubmit = async (data) => {
        const payload = {
            customer_id,
            service: data.title,
            tax: "",
            unit: "",
            price: data.price,
            hsn_sac: data.hascCode,
            description: "",
            category: "",
            gst: data.gst,
            login_user_id: loginUserId,
        };

        addProduct(payload, {
            onSuccess: (res) => {
                if (res?.status === "success") {
                    toast.success(res.message || "Product added successfully");
                    const newProductId = res.data;
                    if (newProductId) {
                        setLatestProductId(Number(newProductId));
                    }
                    setShowModal(false);
                    resetProduct();
                    fetchProducts();
                } else {
                    toast.error(res.message || "Failed to add product");
                }
            },
            onError: (err) => {
                toast.error(err?.message || "Add product request failed");
            },
        });
    };


    // Fetch lead sources when form opens
    const fetchLeadSource = async () => {
        try {
            const res = await GetRequest(`masters/getAllLead_source/${customer_id}`, { headers }, extras);

            if (res?.data) setLeadSource(res.data);
        } catch (error) {
            // Error handling
        }
    };

    // Fetch products and lead sources when form opens
    useEffect(() => {
        if (customer_id && show) {
            fetchProducts();
            fetchLeadSource();
        }
    }, [customer_id, show]);

    // Handle form close
    const handleClose = () => {
        setContactDetails(null);
        setSelectedContactId("");
        setRecentContact({ recentContactId: "", recentContactName: "" });
        setExpectedDate("");
        setExpectedTime("");
        setIsContactSelected(false);
        setHasFormChanges(false);
        setOriginalFormData(null);
        clearErrors();
        setShowExtraSections(true);
        onHide();
    };

    // Generate sidebar items based on contact selection
    const getSidebarItems = () => {

        const baseItems = [
            { id: "Requirment", label: "Requirement", show: true },
            { id: "BasicInformation", label: "General Information", show: true }
        ]

        if (isContactSelected) {
            baseItems.push(
                { id: "Communication", label: "Communication", show: !showExtraSections },
                { id: "Location", label: "Location", show: !showExtraSections },
                { id: "Social", label: "Social", show: !showExtraSections },
                { id: "BussinessInformation", label: "Business Information", show: !showExtraSections },
            )

        }

        return baseItems.filter((item) => item.show)
    }
    return (
        <>
            <style>
                {`
        .w-75::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .w-75::-webkit-scrollbar-track {
          background: transparent;
        }
        .w-75::-webkit-scrollbar-thumb {
          background-color: rgb(122, 58, 94);
          border-radius: 4px;
        }
        .w-75::-webkit-scrollbar-thumb:hover {
          background-color: rgb(102, 48, 79);
        }
        
        /* Fixed form container size */
        .form-container {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }
        
        /* Fixed offcanvas width */
        .offcanvas.w-75 {
          width: 75% !important;
          max-width: 75% !important;
          min-width: 75% !important;
        }
      `}
            </style>
            <Offcanvas
                className="w-75"
                placement="end"
                backdrop="static"
                enforceFocus={true}
                show={show}
                onHide={handleClose}
                keyboard={!isDataFetching}
            >
                <Offcanvas.Header className="border-bottom d-flex justify-content-between align-items-center border-dashed pt-3 pb-0">
                    <div className="pt-0 ps-4">
                        <Offcanvas.Title style={{ color: "rgb(7, 145, 255)", fontWeight: 500 }} className="addEnquiryHead">Edit Deal</Offcanvas.Title>
                        <p style={{ fontSize: "14px", color: "#b26792" }} className="text-secondary">
                            Edit potential deals here.
                        </p>
                    </div>
                    <div className="d-flex align-items-baseline gap-2">
                        <div className=" ">
                            <Form.Check
                                type="switch"
                                id="toggle-extra"
                                label="Required Fields."
                                checked={showExtraSections}
                                onChange={() => setShowExtraSections(!showExtraSections)}
                            />
                        </div>
                        <button
                            style={{ cursor: isDataFetching ? "not-allowed" : "pointer", border: "none", background: "transparent" }}
                            disabled={isDataFetching}
                            onClick={() => {
                                if (!isDataFetching) handleClose();
                            }}
                        >
                            <IconX size={20} stroke={2} />
                        </button>
                    </div>
                </Offcanvas.Header>

                <Offcanvas.Body style={{ pointerEvents: isDataFetching ? "none" : "auto", position: "relative" }}>
                    {isDataFetching && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(255,255,255,0.7)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 1050,
                                pointerEvents: "all",
                            }}
                        >
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    )}
                    <div style={{ display: "flex", height: "100%" }}>
                        {/* Sidebar */}
                        <div style={{ width: "280px", minWidth: "280px", maxWidth: "280px" }} className="border-end">
                            <ul className="ps-3 update-deal-sidebar" style={{ listStyle: "none" }}>
                                {getSidebarItems().map((item, index) => (
                                    <li
                                        key={item.id}
                                        className="position-relative border-start ps-4 py-2"
                                        style={{
                                            cursor: "pointer",
                                            opacity: item.show ? 1 : 0.5,
                                            pointerEvents: item.show ? "auto" : "none",
                                        }}
                                    >
                                        <span
                                            className="position-absolute start-0 translate-middle rounded-circle"
                                            style={{
                                                width: 10,
                                                height: 10,
                                                top: index === 0 ? 20 : "50%",
                                                backgroundColor: "rgb(7, 145, 255)",
                                                border: "1px solid rgb(7, 145, 255)",
                                            }}
                                        />

                                        <a
                                            onClick={() => scrollToSection(item.id)}
                                            style={{
                                                color: "#676262",
                                                textDecoration: "none",
                                                fontSize: "14px",
                                            }}
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <Form
                            className="form-container"
                            onSubmit={handleSubmit(onSubmit)}
                            style={{
                                margin: 0,
                                overflowY: "auto",
                                height: "100%",
                                paddingLeft: "20px",
                                flex: 1,
                                minWidth: 0,
                                paddingBottom: "5rem",
                                opacity: isDataFetching ? 0.6 : 1,
                            }}
                            encType="multipart/form-data"
                        >
                            <div>
                                {/* Requirement Section */}
                                <h4 id="Requirment" style={{ color: "rgb(7, 145, 255)", fontWeight: 300, fontSize: "20px" }}>
                                    Requirement
                                </h4>
                                <Form.Group className="mb-3 focus-z-index position-relative" controlId="formDealName">
                                    <Form.Label style={{ marginBottom: "2px", marginTop: "2px" }} className="w-auto">
                                        Deal Name <span className="text-danger">*</span>
                                    </Form.Label>
                                    <InputGroup >
                                        <Form.Control
                                            type="text"
                                            {...register('deal_name')}
                                        />
                                    </InputGroup>
                                    {
                                        errors.deal_name &&
                                        <span className="text-danger mt-1 fs-12">{errors.deal_name.message}</span>
                                    }
                                </Form.Group>
                                <Form.Group className="mb-3 focus-z-index position-relative" controlId="formCurrency">
                                    <Form.Label style={{ marginBottom: "2px", marginTop: "2px" }} className="w-auto">
                                        Currency <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Controller
                                        name="currency_id"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={currencyOpt}
                                                value={
                                                    field.value
                                                        ? currencyOpt.find(option => option.value === field.value) || null
                                                        : null
                                                }
                                                onChange={(selectedOption) => {
                                                    field.onChange(selectedOption ? selectedOption.value : "");
                                                }}
                                                onBlur={field.onBlur}
                                                isSearchable={true}
                                                isClearable={true}
                                                styles={customStyles}
                                                classNamePrefix="react-select"
                                                placeholder="Select Currency.."
                                            />
                                        )}
                                    />
                                    {
                                        errors.currency_id &&
                                        <span className="text-danger  mt-1 fs-12">{errors.currency_id.message}</span>
                                    }
                                </Form.Group>
                                <Form.Group className="mb-3 focus-z-index position-relative" controlId="requirement">
                                    <Form.Label>
                                        Requirement <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Enter requirement"
                                        {...register("requirement", {
                                            required: "Requirement is required",
                                        })}
                                    />
                                    {errors.requirement && <span className="text-danger mt-1 fs-12">{errors.requirement.message}</span>}
                                </Form.Group>

                                {/* Dynamic Product Fields */}
                                <DynamicProductFields
                                    ref={productFieldsRef}
                                    control={control}
                                    register={register}
                                    setValue={setValue}
                                    watch={watch}
                                    clearErrors={clearErrors}
                                    trigger={trigger}
                                    errors={errors}
                                    products={products}
                                    setShowModal={setShowModal}
                                    dealMasterGrant={dealMasterGrant}
                                />
                            </div>
                            <div style={{ paddingTop: 10 }}>
                                <h4 id="BasicInformation" style={{ color: "rgb(7, 145, 255)", fontWeight: 300, fontSize: "20px" }}>
                                    General Information
                                </h4>
                                <div className="row">
                                    <Col md={6}>
                                        <Form.Group className="mb-3 position-relative focus-z-index" >
                                            <Form.Label className="fw-lighter w-auto">
                                                Contact Name <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Controller
                                                name="contact_name"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        className="basic-single"
                                                        options={
                                                            module_name === "company"
                                                                ? contactArray.map((contact) => ({
                                                                    value: contact.id,
                                                                    label: `${contact.first_name} ${contact.last_name}`,
                                                                }))
                                                                : customContactOptions
                                                        }
                                                        value={
                                                            // contactId || 
                                                            watch("contact_name")
                                                                ? {
                                                                    // value: contactId || watch("contact_name"),
                                                                    value: watch("contact_name"),
                                                                    label:
                                                                        module_name === "company"
                                                                            ? (() => {
                                                                                const contact = contactArray.find(
                                                                                    (c) =>
                                                                                        c.id ===
                                                                                        (
                                                                                            watch("contact_name"))
                                                                                );
                                                                                return contact
                                                                                    ? `${contact.first_name} ${contact.last_name}`
                                                                                    : "Select Contact Name";
                                                                            })()
                                                                            : customContactOptions.find(
                                                                                (c) =>
                                                                                    c.value ===
                                                                                    (watch("contact_name"))
                                                                            )?.label || "Select Contact Name",
                                                                }
                                                                : null
                                                        }
                                                        onChange={(selectedOption) => {
                                                            const newValue = selectedOption
                                                                ? selectedOption.value
                                                                : "";
                                                            setValue("contact_name", newValue);
                                                            setSelectedContactId(newValue);
                                                        }}
                                                        components={{
                                                            MenuList,
                                                        }}
                                                        onAddNew={() => {
                                                            setShowContact(true);
                                                            setShow && setShow(false)
                                                        }}
                                                        show={module_name === "company" ? false : true}
                                                        styles={customStyles}
                                                        isSearchable={true}
                                                        isClearable={true}
                                                        classNamePrefix="react-select"
                                                        placeholder="Select Contact Name"
                                                        moduleType={"contact"}
                                                        canCreate={dealContactGrant.isCreate}
                                                    />
                                                )}
                                            />
                                            {errors.contact_name && <p className="text-danger mt-1 fs-12">Contact name is required</p>}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3 position-relative focus-z-index">
                                            <Form.Label className="fw-lighter">
                                                Pipeline <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Controller
                                                name="pipeline"
                                                control={control}
                                                rules={{ required: "Pipeline is required" }}
                                                render={({ field }) => {
                                                    const selectedOption = availablePipelines.find((p) => String(p.id) === String(field.value))

                                                    return (
                                                        <Select
                                                            options={availablePipelines.map((pipeline) => ({
                                                                value: String(pipeline.id),
                                                                label: pipeline.pipeline_name,
                                                            }))}
                                                            value={
                                                                selectedOption
                                                                    ? { value: String(selectedOption.id), label: selectedOption.pipeline_name }
                                                                    : null
                                                            }
                                                            onChange={(selected) => {
                                                                const newPipelineId = selected ? Number(selected.value) : null
                                                                field.onChange(selected ? selected.value : null)
                                                                setSelectedPipelineId(newPipelineId)
                                                                // Reset pipeline stage when pipeline changes
                                                                setValue("pipeline_stage", "")
                                                                if (selected) {
                                                                    clearErrors("pipeline")
                                                                }
                                                            }}
                                                            isSearchable
                                                            isClearable
                                                            styles={customStyles}
                                                            placeholder="Select Pipeline"
                                                            classNamePrefix="react-select"
                                                        />
                                                    )
                                                }}
                                            />
                                            {errors.pipeline && <p className="text-danger mt-1 fs-12">Pipeline is required</p>}
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group className="mb-3 position-relative focus-z-index">
                                            <Form.Label className="fw-lighter">
                                                Pipeline Stage<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Controller
                                                name="pipeline_stage"
                                                control={control}
                                                rules={{ required: "Pipeline stage is required" }}
                                                render={({ field }) => {
                                                    const selectedOption = pipeLine.find((p) => String(p.id) === String(field.value))

                                                    return (
                                                        <Select
                                                            options={pipeLine.map((stage) => ({
                                                                value: String(stage.id),
                                                                label: stage.stage_name,
                                                            }))}
                                                            value={
                                                                selectedOption
                                                                    ? { value: String(selectedOption.id), label: selectedOption.stage_name }
                                                                    : null
                                                            }
                                                            onChange={(selected) => {
                                                                field.onChange(selected ? selected.value : null)
                                                                if (selected) {
                                                                    clearErrors("pipeline_stage")
                                                                }
                                                            }}
                                                            isSearchable
                                                            isClearable
                                                            styles={customStyles}
                                                            placeholder={selectedPipelineId ? "Select Pipeline Stage" : "Select Pipeline First"}
                                                            classNamePrefix="react-select"
                                                            isDisabled={!selectedPipelineId || pipeLine.length === 0}
                                                        />
                                                    )
                                                }}
                                            />
                                            {/* Show error even when disabled if pipeline is selected but stage is not */}
                                            {errors.pipeline_stage && (
                                                <p className="text-danger mt-1 fs-12">{errors.pipeline_stage.message}</p>
                                            )}
                                            {/* Show helper text when pipeline is not selected */}
                                            {!selectedPipelineId && (
                                                <p className="text-muted mt-1 fs-12" style={{ fontSize: "0.875rem" }}>
                                                    Please select a pipeline first
                                                </p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </div>

                                <div className="row">
                                    <Col md={6}>
                                        <Form.Group className="mb-3 position-relative focus-z-index">
                                            <Form.Label className="fw-lighter">Owner</Form.Label>
                                            <Controller
                                                name="owner"
                                                control={control}
                                                rules={{ required: "Owner is required" }}
                                                render={({ field }) => (
                                                    <Select
                                                        className="basic-single"
                                                        options={ownerOptions.map((owner) => ({ value: owner.id, label: owner.label }))}
                                                        value={
                                                            watch("owner")
                                                                ? {
                                                                    value: watch("owner"),
                                                                    label: ownerOptions.find((o) => o.id === watch("owner"))?.label || "Select Owner",
                                                                }
                                                                : null
                                                        }
                                                        onChange={(selectedOption) => {
                                                            // If cleared, set to first owner
                                                            const newValue = selectedOption
                                                                ? selectedOption.value
                                                                : ownerOptions.length > 0
                                                                    ? ownerOptions[0].id
                                                                    : ""
                                                            setValue("owner", newValue)
                                                        }}
                                                        isDisabled={!isOwnerSame}
                                                        isSearchable={true}
                                                        isClearable={true}
                                                        styles={customStyles}
                                                        classNamePrefix="react-select"
                                                        placeholder="Select Owner"
                                                    />
                                                )}
                                            />
                                            {errors.owner && <p className="text-danger mt-1 fs-12">{errors.owner.message}</p>}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3 position-relative focus-z-index ">
                                            <Form.Label className="fw-lighter">
                                                Expected Closure On
                                                <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                {...register("expected_closure_date")}
                                                value={expectedDate}
                                                onChange={(e) => {
                                                    setExpectedDate(e.target.value)
                                                    setValue("expected_closure_date", e.target.value)
                                                    if (e.target.value) {
                                                        clearErrors("expected_closure_date")
                                                    }
                                                }}
                                            />
                                            {errors.expected_closure_date && (
                                                <p className="text-danger mt-1 fs-12">{errors.expected_closure_date.message}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        {/* Expected Closure Time - 6 columns within this section */}
                                        <Form.Group className="mb-3 position-relative focus-z-index ">
                                            <Form.Label className="fw-lighter">Expected Closure Time</Form.Label>
                                            <Form.Control
                                                type="time"
                                                {...register("expected_closure_time")}
                                                value={expectedTime}
                                                onChange={(e) => {
                                                    setExpectedTime(e.target.value)
                                                    setValue("expected_closure_time", e.target.value)
                                                    if (e.target.value) {
                                                        clearErrors("expected_closure_time")
                                                    }
                                                }}
                                            />
                                            {errors.expected_closure_time && (
                                                <p className="text-danger mt-1 fs-12">{errors.expected_closure_time.message}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </div>
                            </div>
                            <hr />
                            {isContactSelected && !showExtraSections && (
                                <>
                                    {/* Communication Section */}
                                    <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                                        <h4 id="Communication" style={{ color: "rgb(7, 145, 255)", fontWeight: 300, fontSize: "20px" }}>
                                            Communication
                                        </h4>

                                        <div className="pt-3 d-flex flex-column">
                                            <div className="row">
                                                {/* Primary Email */}
                                                <div className="col-md-6">
                                                    <Form.Group className="mb-3 focus-z-index position-relative" controlId="formEmail">
                                                        <Form.Label className="w-auto">
                                                            Email<span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <div className="d-flex gap-2">
                                                            <Form.Select
                                                                value={JSON.parse(contactDetails?.user_email ?? "[]")[0]?.email_type || "Office"}
                                                                style={{ width: "30%", fontSize: "14px" }}
                                                                disabled
                                                            >
                                                                <option value="Office">Office</option>
                                                                <option value="Personal">Personal</option>
                                                            </Form.Select>

                                                            <Form.Control
                                                                type="email"
                                                                value={JSON.parse(contactDetails?.user_email ?? "[]")[0]?.email || ""}
                                                                placeholder="ex: abc@gmail.com"
                                                                {...register("primary_email")}
                                                                disabled
                                                            />
                                                        </div>
                                                        {errors.primary_email && <p className="text-danger mt-1 fs-12">{errors.primary_email.message}</p>}
                                                    </Form.Group>
                                                </div>

                                                {/* Phone */}
                                                <div className="col-md-6">
                                                    <Form.Group className="mb-3 focus-z-index position-relative" controlId="formContact">
                                                        <Form.Label className="w-auto">
                                                            Contact <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <div className="d-flex gap-2">
                                                            <Form.Select
                                                                value={JSON.parse(contactDetails?.user_phone ?? "[]")[0]?.phone_type || "Mobile"}
                                                                style={{ width: "30%", fontSize: "14px" }}
                                                                disabled
                                                            >
                                                                <option value="Mobile">Mobile</option>
                                                                <option value="Work">Work</option>
                                                                <option value="Home">Home</option>
                                                                <option value="Personal">Personal</option>
                                                            </Form.Select>

                                                            <Controller
                                                                className="bg-primary"
                                                                control={control}
                                                                disabled
                                                                name="primary_contact_number"
                                                                render={({ field }) => {
                                                                    const phoneData = JSON.parse(contactDetails?.user_phone ?? "[]")[0] || {}
                                                                    const fullNumber = `+${phoneData.country_code || "91"}${phoneData.phone || ""}`
                                                                    return (
                                                                        <PhoneInput
                                                                            disabled
                                                                            country={"in"}
                                                                            value={fullNumber}
                                                                            onChange={() => { }}
                                                                            inputProps={{
                                                                                readOnly: true,
                                                                            }}
                                                                            inputStyle={{
                                                                                width: "235px",
                                                                                height: "38px",
                                                                            }}
                                                                            countryCodeEditable={false}
                                                                            buttonStyle={{
                                                                                backgroundColor: "#e9ecef",
                                                                                border: "1px solid #ced4da",
                                                                                borderRight: "none",
                                                                                borderRadius: "4px 0 0 4px",
                                                                            }}
                                                                        />
                                                                    )
                                                                }}
                                                            />
                                                        </div>

                                                        {errors.primary_contact_number && (
                                                            <p className="text-danger mt-1 fs-12">{errors.primary_contact_number.message}</p>
                                                        )}
                                                    </Form.Group>
                                                </div>
                                            </div>

                                            {/* Timezone */}
                                            <div style={{ marginTop: "10px", width: "46rem" }}>
                                                <Form.Group className="mb-3 focus-z-index position-relative" controlId="formTimezone">
                                                    <Form.Label className="w-auto">Select Timezone</Form.Label>
                                                    <Controller
                                                        name="timezone"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TimezoneSelect
                                                                value={JSON.parse(contactDetails?.timezone ?? "{}")}
                                                                onChange={() => { }}
                                                                isDisabled={true}
                                                                timezones={{
                                                                    ...allTimezones,
                                                                    "America/Lima": "Pittsburgh",
                                                                    "Europe/Berlin": "Frankfurt",
                                                                }}
                                                                styles={customStyles}
                                                            />
                                                        )}
                                                    />
                                                    {errors.timezone && <p className="text-danger mt-1 fs-12">{errors.timezone.message}</p>}
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </div>
                                    <hr />

                                    {/* Location Section */}
                                    <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                                        <h4 id="Location" style={{ color: "rgb(7, 145, 255)", fontWeight: 300, fontSize: "20px" }}>
                                            Location
                                        </h4>

                                        <Form.Group className="mb-3 focus-z-index position-relative" controlId="formAddress">
                                            <Form.Label style={{ marginBottom: "2px", marginTop: "2px" }} className="w-auto">
                                                Address
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    as="textarea"
                                                    value={contactDetails?.address || ""}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                            </InputGroup>
                                        </Form.Group>

                                        <div className="row">
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formCountryReadOnly">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Country</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={getCountryName(contactDetails?.country) || ""}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                            </Form.Group>


                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formStateReadOnly">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">State</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={getStateName(contactDetails?.state) || ""}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                            </Form.Group>
                                        </div>

                                        <div className="row">
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formCityReadOnly">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">City</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={getCityName(contactDetails?.city) || ""}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formZipcodeReadOnly">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Zipcode</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={contactDetails?.zipcode || ""}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <hr />

                                    {/* Social Links Section */}
                                    <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                                        <h4 id="Social" style={{ color: "rgb(7, 145, 255)", fontWeight: 300, fontSize: "20px" }}>
                                            Social
                                        </h4>

                                        <div className="row">
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formFacebook">
                                                <Form.Label style={{ marginBottom: 1, marginLeft: '11px' }} className="w-auto">
                                                    Facebook
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter URL"
                                                        value={contactDetails?.facebook || ""}
                                                        {...register("facebook")}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formLinkedin">
                                                <Form.Label style={{ marginBottom: 1, marginLeft: '11px' }} className="w-auto">
                                                    LinkedIn
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter URL"
                                                        value={contactDetails?.linkedin || ""}
                                                        {...register("linkedin")}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </div>

                                        <div className="row">
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formInstagram">
                                                <Form.Label style={{ marginBottom: 1, marginLeft: '11px' }} className="w-auto">
                                                    Instagram
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter URL"
                                                        value={contactDetails?.instagram || ""}
                                                        {...register("instagram")}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formTwitter">
                                                <Form.Label style={{ marginBottom: 1, marginLeft: '11px' }} className="w-auto">
                                                    Twitter
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter URL"
                                                        value={contactDetails?.twitter || ""}
                                                        {...register("twitter")}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <hr />

                                    {/* Business Information Section */}
                                    <div style={{ paddingLeft: 0, paddingTop: 10 }}>
                                        <h4 id="BussinessInformation" style={{ color: "rgb(7, 145, 255)", fontWeight: 300, fontSize: "20px" }}>
                                        </h4>

                                        <div className="row">
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formCompanyName">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Company Name</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        value={contactDetails?.company_name || ""}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formIndustry">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Company Industry</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        value={contactDetails?.industry || ""}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formBusinessType">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Business Type</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        value={contactDetails?.business_type || ""}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formCompanyWebsite">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Company Website</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        value={contactDetails?.website || ""}
                                                        readOnly
                                                        style={{ backgroundColor: "#e9ecef" }}
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Group className="mb-3 col-md-12 position-relative focus-z-index" controlId="formEmail">
                                                    <Form.Label className="w-auto">Email</Form.Label>
                                                    <div className="d-flex gap-2">
                                                        <Form.Select
                                                            disabled
                                                            value={JSON.parse(contactDetails?.user_email ?? "[]")[0]?.email_type || "Office"}
                                                            style={{
                                                                width: "30%",
                                                                fontSize: "14px",
                                                                backgroundColor: "#e9ecef",
                                                            }}
                                                        >
                                                            <option value="Office">Office</option>
                                                        </Form.Select>
                                                        <Form.Control
                                                            type="email"
                                                            value={JSON.parse(contactDetails?.user_email ?? "[]")[0]?.email || ""}
                                                            readOnly
                                                            style={{ backgroundColor: "#e9ecef" }}
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </div>

                                            <div className="col-md-6">
                                                <Form.Group className="mb-3 position-relative focus-z-index" controlId="formContact">
                                                    <Form.Label className="w-auto">Contact</Form.Label>
                                                    <div className="d-flex gap-2">
                                                        <Form.Select
                                                            disabled
                                                            value="Mobile"
                                                            style={{
                                                                width: "30%",
                                                                fontSize: "14px",
                                                            }}
                                                        >
                                                            <option value="Mobile">Mobile</option>
                                                        </Form.Select>
                                                        <PhoneInput
                                                            disabled
                                                            country={"in"}
                                                            value={contactDetails?.company_phones || ""}
                                                            inputStyle={{ width: "235px", height: "38px" }}
                                                            countryCodeEditable={false}
                                                            inputProps={{ readOnly: true }}
                                                            buttonStyle={{
                                                                backgroundColor: "#e9ecef",
                                                                border: "1px solid #ced4da",
                                                                borderRight: "none",
                                                                borderRadius: "4px 0 0 4px",
                                                            }}
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </div>
                                        </div>

                                        <Form.Group className="mb-3 d-flex flex-column position-relative focus-z-index" controlId="formAddress">
                                            <Form.Label className="w-auto">Company Address</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    as="textarea"
                                                    value={contactDetails?.address || ""}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                            </InputGroup>
                                        </Form.Group>

                                        <div className="row">
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formCountry">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Country</Form.Label>
                                                <Form.Select
                                                    value={contactDetails?.company_country || ""}
                                                    disabled
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                >
                                                    <option value=""></option>
                                                    {country.map((c) => (
                                                        <option key={c.countryId} value={c.countryId}>
                                                            {c.countryName}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>

                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formState">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">State</Form.Label>
                                                <Form.Select
                                                    value={contactDetails?.company_state || ""}
                                                    disabled
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                >
                                                    <option value=""></option>
                                                    {states.map((s, index) => (
                                                        <option key={index} value={s.stateCode}>
                                                            {s.stateName}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </div>

                                        <div className="row">
                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formCity">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">City</Form.Label>
                                                <Form.Select
                                                    value={contactDetails?.company_city || ""}
                                                    disabled
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                >
                                                    <option value=""></option>
                                                    {city.map((c, index) => (
                                                        <option key={index} value={c.cityCode}>
                                                            {c.cityName}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>

                                            <Form.Group className="mb-3 col-md-6 position-relative focus-z-index" controlId="formZipcode">
                                                <Form.Label style={{ marginLeft: '11px' }} className="w-auto">Zipcode</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={contactDetails?.company_zipcode || ""}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div
                                className="sticky-buttons"
                                style={{
                                    position: "fixed",
                                    width: "calc(75% - 300px)",
                                    bottom: 0,
                                    right: 0,
                                    height: "80px",
                                    zIndex: 1000,
                                    backgroundColor: "white",
                                    borderTop: "1px solid #dee2e6",
                                    paddingRight: "20px",
                                    paddingLeft: "20px",
                                }}
                            >
                                <div className="d-flex justify-content-end gap-4 pt-4">
                                    <Button
                                        onClick={handleClose}
                                        className="btn btn-default-outline"
                                    >
                                        Cancel
                                    </Button>
                                    <Button className="btn btn-default" type="submit">Update</Button>
                                </div>
                            </div>
                        </Form>
                        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Add Product or Services</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form
                                    onSubmit={handleSubmitProduct((data, event) => {
                                        event?.preventDefault();
                                        event?.stopPropagation();
                                        onProductSubmit(data);
                                    })}
                                >
                                    <Form.Group>
                                        <Form.Label>Deal Product or Services Title</Form.Label>
                                        <span className="text-danger">*</span>
                                        <Form.Control
                                            type="text"
                                            placeholder="Product or Services Title"
                                            {...registerProduct("title")}
                                            isInvalid={!!productErrors.title}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {productErrors.title?.message}
                                        </Form.Control.Feedback>

                                        <Form.Label className="w-auto">Price</Form.Label>
                                        <span className="text-danger">*</span>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter price"
                                            {...registerProduct("price")}
                                            isInvalid={!!productErrors.price}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {productErrors.price?.message}
                                        </Form.Control.Feedback>

                                        <Form.Label className="w-auto mt-3">GST (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter GST percentage"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            {...registerProduct("gst")}
                                            isInvalid={!!productErrors.gst}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {productErrors.gst?.message}
                                        </Form.Control.Feedback>

                                        <Form.Label className="w-auto">HSN or SAC Code</Form.Label>
                                        <span className="text-danger">*</span>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter HSN or SAC Code"
                                            {...registerProduct("hascCode")}
                                            isInvalid={!!productErrors.hascCode}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {productErrors.hascCode?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Modal.Footer className="px-0 mt-3">
                                        <CancelBtn
                                            type={'button'}
                                            onClick={handleClose}
                                            submitClicked={isSaving}
                                        />
                                        <SubmitBtn
                                            type={'submit'}
                                            title={"Update"}
                                            loading={isSaving}
                                        />

                                    </Modal.Footer>
                                </Form>
                            </Modal.Body>
                        </Modal>
                    </div>
                </Offcanvas.Body>
            </Offcanvas >
            {
                <ContactForm
                    onClose={() => setShow(true)}
                    show={contactShow}
                    setShow={setShowContact}
                    setRecentContactId={setRecentContact}
                    // companyGrants={companyGrants}
                    country={country}
                    masterGrant={dealMasterGrant}
                />
            }
        </>
    );
};